import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';

const stappen = [
  { icon: 'ri-message-3-line', step: '01', title: 'Stuur uw product door', desc: 'Beschrijf uw product, stuur een foto of link. Wij nemen contact op en beginnen direct met zoeken.' },
  { icon: 'ri-search-2-line', step: '02', title: 'Wij vinden de goedkoopste fabriek', desc: 'Direct uit China inkopen bij de bron — geen tussenhandel, geen importeur. Puur fabrieksprijzen.' },
  { icon: 'ri-file-list-3-line', step: '03', title: 'Gratis prijsindicatie binnen 24u', desc: 'U ontvangt een transparante, all-in offerte. Geen verborgen kosten, geen verplichtingen.' },
  { icon: 'ri-truck-line', step: '04', title: 'Goedkoop geleverd aan uw deur', desc: 'All-in DDP levering — alles inbegrepen. U betaalt één bedrag, wij regelen de rest.' },
];

const prijsvergelijking = [
  { product: 'Geborduurde T-shirts', china: '€6 – €9', nederland: '€25 – €45', besparing: '70–80%' },
  { product: 'Bedrukte koffiebekers', china: '€0,80 – €1,50', nederland: '€4 – €8', besparing: '65–80%' },
  { product: 'Branded shakebekers', china: '€2 – €4', nederland: '€12 – €20', besparing: '70–80%' },
  { product: 'EV laadkabels 22kW', china: '€65 – €90', nederland: '€220 – €350', besparing: '65–75%' },
  { product: 'Papieren draagtassen op maat', china: '€0,30 – €0,60', nederland: '€1,50 – €3', besparing: '70–80%' },
  { product: 'Luxe wandlampen', china: '€35 – €60', nederland: '€180 – €280', besparing: '70–80%' },
  { product: 'E-choppers', china: '€800 – €1.200', nederland: '€2.500 – €5.000', besparing: '60–75%' },
  { product: 'Bedrukte sportkleding', china: '€5 – €10', nederland: '€25 – €50', besparing: '70–80%' },
];

const producten = [
  { icon: 'ri-t-shirt-2-line', label: 'Kleding & textiel', href: '/blog/geborduurde-tshirts-inkopen-china' },
  { icon: 'ri-store-2-line', label: 'Horeca supplies', href: '/voor-wie/horeca' },
  { icon: 'ri-shopping-cart-line', label: 'Webshop producten', href: '/voor-wie/webshops' },
  { icon: 'ri-lightbulb-line', label: 'Verlichting & interieur', href: '/blog/luxe-wandlampen-inkopen-china' },
  { icon: 'ri-vip-diamond-line', label: 'Sieraden & juwelen', href: '/voor-wie/juweliers' },
  { icon: 'ri-medal-line', label: 'Sport & fitnessproducten', href: '/voor-wie/gyms' },
  { icon: 'ri-charging-pile-2-line', label: 'Elektronica & gadgets', href: '/blog/ev-laadkabels-inkopen-china' },
  { icon: 'ri-paint-brush-line', label: 'Private label', href: '/private-label-china' },
  { icon: 'ri-briefcase-4-line', label: 'B2B & promotiemateriaal', href: '/voor-wie/bedrijven' },
  { icon: 'ri-building-4-line', label: 'Retail & groothandel', href: '/voor-wie/retail' },
  { icon: 'ri-motorbike-line', label: 'E-mobility producten', href: '/blog/e-choppers-inkopen-china' },
  { icon: 'ri-newspaper-line', label: 'Drukwerk & verpakkingen', href: '/blog/bedrukte-paper-bags-inkopen-china' },
];

const voordelen = [
  { icon: 'ri-money-euro-circle-line', title: 'Tot 80% goedkoper inkopen', desc: 'Goedkoop inkopen uit China is mogelijk door directe toegang tot fabrikanten — geen importeurs, geen tussenpersonen.' },
  { icon: 'ri-shield-check-line', title: 'Veilig en betrouwbaar', desc: 'Alleen geverifieerde fabrieken. Kwaliteitscontroles voor en na productie zodat u nooit voor verrassingen staat.' },
  { icon: 'ri-truck-line', title: 'All-in DDP levering', desc: 'Goedkoop inkopen én bezorgd aan uw deur. Invoerrechten, douane en transport volledig inbegrepen.' },
  { icon: 'ri-time-line', title: 'Gratis offerte in 24 uur', desc: 'Vraag nu aan en ontvang een gratis, vrijblijvende prijsindicatie voor uw product binnen 24 uur.' },
  { icon: 'ri-paint-brush-line', title: 'Uw eigen merk & branding', desc: 'Goedkoop inkopen hoeft niet te betekenen: geen identiteit. Private label en OEM mogelijk voor vrijwel elk product.' },
  { icon: 'ri-team-line', title: 'Persoonlijke begeleiding', desc: 'Van offerte tot levering regelt DUC alles. U hoeft niets te weten van Chinees, douane of logistiek.' },
];

const faqs = [
  { q: 'Hoe kan ik goedkoop inkopen uit China?', a: 'Goedkoop inkopen uit China lukt het best door direct bij fabrikanten te kopen via een sourcing agent zoals DUC. Wij omzeilen tussenhandel en importeurs, waardoor u 30–80% goedkoper inkoopt. Stuur ons uw product en ontvang een gratis prijsindicatie.' },
  { q: 'Is goedkoop inkopen uit China ook voor kleine bedrijven mogelijk?', a: 'Ja, absoluut. DUC werkt met kleine orders. Veel fabrieken beginnen al bij 50–100 stuks. Of u nu een kleine webshop runt of een grote retailer bent — goedkoop inkopen uit China is voor iedereen toegankelijk via DUC.' },
  { q: 'Hoe weet ik dat de goedkope producten uit China ook kwalitatief zijn?', a: 'DUC werkt alleen met geverifieerde fabrieken. We voeren steekproefsgewijze kwaliteitscontroles uit voor en na productie. Goedkoop hoeft echt niet slecht te betekenen — Chinese fabrikanten produceren ook voor grote Europese merken.' },
  { q: 'Wat zijn de kosten van goedkoop inkopen via DUC?', a: 'De eerste prijsindicatie is gratis en vrijblijvend. DUC werkt transparant — soms kopen wij zelf in en verkopen door, soms op commissiebasis. Alle prijzen zijn exclusief BTW en er zijn geen verborgen kosten.' },
  { q: 'Hoe lang duurt het voordat ik goedkoop ingekochte producten uit China ontvang?', a: 'Via zeecontainer 8–16 weken (inclusief productietijd). Via luchtvervoer 1–3 weken. Wij geven altijd een realistische inschatting voordat u bestelt, zodat u nooit voor verrassingen staat.' },
  { q: 'Welke producten kan ik goedkoop inkopen uit China via DUC?', a: 'Vrijwel alles: kleding, horecabenodigdheden, elektronica, sieraden, verpakkingen, promotiemateriaal, sportartikelen en meer. Goedkoop inkopen uit China werkt voor bijna elke productcategorie.' },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border transition-all duration-200 overflow-hidden ${open ? 'border-[#3B9EE8]/40 bg-white' : 'border-[#0A1628]/8 bg-white hover:border-[#3B9EE8]/25'}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer">
        <span className={`font-500 text-base pr-8 leading-snug ${open ? 'text-[#0A1628] font-600' : 'text-[#0A1628]/80'}`}>{q}</span>
        <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${open ? 'bg-[#3B9EE8] rotate-45' : 'bg-[#0A1628]/6'}`}>
          <i className={`ri-add-line text-sm ${open ? 'text-white' : 'text-[#0A1628]/60'}`} />
        </div>
      </button>
      {open && (
        <div className="px-7 pb-6 border-t border-[#3B9EE8]/15">
          <p className="text-[#0A1628]/65 text-base leading-relaxed pt-4">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function GoedkoopInkopenChinaPage() {
  useSEO({
    title: 'Goedkoop Inkopen uit China | 30–80% Besparen | DUC Sourcing Agent',
    description: 'Goedkoop inkopen uit China via DUC. Direct bij fabrikanten inkopen zonder tussenhandel. Bespaar 30–80% op uw inkoopkosten. Gratis prijscheck binnen 24 uur. DDP levering aan uw deur.',
    keywords: 'goedkoop inkopen china, goedkoop inkopen uit china, goedkoop producten inkopen china, direct uit china inkopen, fabrikant china inkopen, goedkoop importeren china, cheap inkopen china',
    canonical: `${SITE_URL}/goedkoop-inkopen-china`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Goedkoop Inkopen China', item: `${SITE_URL}/goedkoop-inkopen-china` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Goedkoop Inkopen uit China — 30–80% besparen via DUC',
        description: 'Goedkoop inkopen uit China via sourcing agent DUC. Direct bij fabrikanten inkopen zonder tussenhandel. Bespaar 30–80% op uw inkoopkosten.',
        url: `${SITE_URL}/goedkoop-inkopen-china`,
        inLanguage: 'nl-NL',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Goedkoop Inkopen China — DUC Sourcing Agent',
        description: 'DUC helpt bedrijven en particulieren goedkoop inkopen uit China. Direct bij fabrikanten inkopen, kwaliteitscontrole en DDP levering.',
        provider: {
          '@type': 'Organization',
          name: 'Direct Uit China (DUC)',
          url: SITE_URL,
        },
        areaServed: { '@type': 'Country', name: 'Netherlands' },
        serviceType: 'Sourcing Agent China',
      },
    ],
  });

  const heroRef = useRef<HTMLDivElement>(null);
  const heroInView = useInView(heroRef, { once: true });
  const stepsRef = useRef<HTMLDivElement>(null);
  const stepsInView = useInView(stepsRef, { once: true, margin: '-80px' });
  const tabelRef = useRef<HTMLDivElement>(null);
  const tabelInView = useInView(tabelRef, { once: true, margin: '-80px' });
  const voordelenRef = useRef<HTMLDivElement>(null);
  const voordelenInView = useInView(voordelenRef, { once: true, margin: '-80px' });
  const productenRef = useRef<HTMLDivElement>(null);
  const productenInView = useInView(productenRef, { once: true, margin: '-80px' });

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="relative bg-[#0A1628] pt-[100px] sm:pt-[116px] lg:pt-[136px] pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/722c57d7ce862957fdc1f4159fa032ec.jpg"
            alt="Goedkoop inkopen uit China — directe fabrieksprijzen via DUC"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628] via-[#0A1628]/85 to-[#0A1628]/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-white/45 hover:text-[#3B9EE8] text-sm mb-8 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line" /> Terug naar home
            </Link>
          </motion.div>

          <motion.span
            initial={{ opacity: 0, y: 14 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6"
          >
            <i className="ri-price-tag-3-line" /> Goedkoop Inkopen &bull; Direct bij Fabrikanten
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="font-heading font-800 text-white text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 max-w-4xl"
          >
            Goedkoop inkopen uit China —<br />
            <span className="text-[#3B9EE8]">30 tot 80% goedkoper</span><br />
            <span className="text-white/75 text-3xl md:text-4xl font-600">direct bij fabrikanten, zonder tussenpersonen</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="text-white/60 text-lg max-w-2xl leading-relaxed mb-8"
          >
            <strong className="text-white/85">DUC (Direct Uit China)</strong> is uw sourcing agent voor goedkoop inkopen uit China. Wij regelen directe toegang tot fabrieken, kwaliteitscontrole en <strong className="text-white/85">all-in DDP levering</strong> aan uw deur in Nederland — zonder verrassingen.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="flex flex-wrap gap-4 mb-10"
          >
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-7 py-3.5 rounded-lg font-700 text-sm transition-all cursor-pointer whitespace-nowrap">
              Start nu goedkoop inkopen
              <i className="ri-arrow-right-line" />
            </Link>
            <a
              href="https://wa.me/31624359162?text=Hi%2C%20ik%20wil%20graag%20goedkoop%20inkopen%20uit%20China%20via%20DUC."
              target="_blank" rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-[#3B9EE8]/60 text-white hover:text-[#3B9EE8] px-6 py-3.5 rounded-lg font-500 text-sm transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base" /> WhatsApp ons nu
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="flex flex-wrap gap-6 pt-8 mt-2 border-t border-white/10"
          >
            {[
              { v: '30–80%', l: 'Goedkoper dan NL' },
              { v: '2.000+', l: 'Geverifieerde fabrieken' },
              { v: '24u', l: 'Gratis prijscheck' },
              { v: 'DDP', l: 'All-in levering' },
              { v: '100+', l: 'Tevreden klanten' },
            ].map((s) => (
              <div key={s.v} className="flex flex-col">
                <span className="font-heading font-800 text-white text-xl">{s.v}</span>
                <span className="text-white/40 text-xs mt-0.5">{s.l}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Uitleg sectie */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
                <i className="ri-question-line" /> Hoe werkt goedkoop inkopen uit China?
              </span>
              <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl leading-tight mb-6">
                Waarom is inkopen uit China<br />
                <strong className="text-[#3B9EE8]">zo veel goedkoper?</strong>
              </h2>
              <div className="space-y-5 text-[#0A1628]/65 text-base leading-relaxed">
                <p>
                  Producten die u in Nederland koopt voor €30, worden in Chinese fabrieken geproduceerd voor €5–€8. Het verschil zit in de keten: importeur, distributeur, groothandel — elke schakel voegt marge toe.
                </p>
                <p>
                  <strong className="text-[#0A1628]">Goedkoop inkopen uit China werkt</strong> door die keten over te slaan. DUC verbindt u rechtstreeks met de fabriek — geen tussenpersoon, geen opslag, geen importmarge. U betaalt de echte fabrieksprijs.
                </p>
                <p>
                  Het risico? Taalbarrière, kwaliteitscontrole en logistiek. Daar is DUC voor: wij zijn uw <strong className="text-[#0A1628]">Nederlandse partner</strong> die alles regelt in China, zodat u veilig en goedkoop kunt inkopen.
                </p>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-3">
                {[
                  { icon: 'ri-building-line', text: 'Direct bij fabrikant' },
                  { icon: 'ri-close-circle-line', text: 'Geen importeurs marge' },
                  { icon: 'ri-shield-check-line', text: 'Kwaliteitscontrole' },
                  { icon: 'ri-truck-line', text: 'All-in levering' },
                ].map((i) => (
                  <div key={i.text} className="flex items-center gap-3 bg-[#F7F8FA] rounded-xl p-4 border border-[#0A1628]/5">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#3B9EE8]/12 flex-shrink-0">
                      <i className={`${i.icon} text-[#3B9EE8] text-sm`} />
                    </div>
                    <span className="text-[#0A1628]/75 text-xs font-500 leading-snug">{i.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative h-96 lg:h-[480px] rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=supply%20chain%20comparison%20infographic%20style%20factory%20to%20consumer%20direct%20route%20Netherlands%20China%20trade%20professional%20clean%20minimal%20diagram%20arrows%20cost%20saving%20price%20difference%20dark%20navy%20background%20white%20elements%20modern%20graphic%20design&width=700&height=480&seq=goedkoop-china-uitleg-002&orientation=landscape"
                alt="Goedkoop inkopen uit China direct bij fabrikant — ketting tussenhandel omzeilen"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#0A1628]/85 to-transparent">
                <p className="text-white font-700 text-sm">Geen tussenhandel = veel goedkoper inkopen</p>
                <p className="text-white/55 text-xs mt-1">Fabriek &rarr; DUC &rarr; Uw deur &bull; Eenvoudig en transparant</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prijsvergelijking tabel */}
      <section ref={tabelRef} className="py-20 bg-[#F7F8FA]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
              <i className="ri-price-tag-2-line" /> Goedkoop inkopen — echte prijzen
            </span>
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-4">
              Hoe goedkoop is inkopen uit China <span className="text-[#3B9EE8]">écht?</span>
            </h2>
            <p className="text-[#0A1628]/55 text-base max-w-xl mx-auto">
              Concrete voorbeelden van wat u bespaart als u goedkoop inkoopt uit China via DUC in plaats van bij een Nederlandse leverancier.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={tabelInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="rounded-2xl overflow-hidden border border-[#0A1628]/8"
          >
            {/* Header */}
            <div className="grid grid-cols-4 bg-[#0A1628] px-6 py-4 text-white text-sm font-700">
              <span>Product</span>
              <span className="text-center text-[#3B9EE8]">Prijs via DUC</span>
              <span className="text-center text-white/50">NL leverancier</span>
              <span className="text-center text-[#3B9EE8]">Besparing</span>
            </div>
            {prijsvergelijking.map((r, i) => (
              <motion.div
                key={r.product}
                initial={{ opacity: 0, x: -16 }}
                animate={tabelInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`grid grid-cols-4 px-6 py-4 text-sm items-center ${i % 2 === 0 ? 'bg-white' : 'bg-[#F7F8FA]/80'}`}
              >
                <span className="text-[#0A1628]/80 font-500">{r.product}</span>
                <span className="text-center font-700 text-[#3B9EE8]">{r.china}</span>
                <span className="text-center text-[#0A1628]/40 line-through">{r.nederland}</span>
                <div className="flex justify-center">
                  <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 rounded-full px-3 py-1 text-xs font-700">
                    <i className="ri-arrow-down-line text-xs" /> {r.besparing}
                  </span>
                </div>
              </motion.div>
            ))}
            <div className="bg-[#0A1628] px-6 py-4 text-white/60 text-xs">
              * Prijzen zijn indicatief. Exacte prijs afhankelijk van volume, specificaties en product. Ex. BTW.
            </div>
          </motion.div>

          <div className="text-center mt-8">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-7 py-3.5 rounded-lg font-700 text-sm transition-all cursor-pointer whitespace-nowrap">
              Bereken goedkoop uw inkoop
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Hoe het werkt */}
      <section ref={stepsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
              <i className="ri-route-line" /> Stap voor stap
            </span>
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl">
              Zo werkt goedkoop inkopen uit China <span className="text-[#3B9EE8]">via DUC</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stappen.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 24 }}
                animate={stepsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-[#F7F8FA] rounded-2xl p-7 border border-[#0A1628]/5 relative"
              >
                <span className="absolute top-5 right-6 font-heading font-900 text-[#0A1628]/5 text-5xl leading-none pointer-events-none select-none">{s.step}</span>
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#3B9EE8]/12 mb-5">
                  <i className={`${s.icon} text-[#3B9EE8] text-xl`} />
                </div>
                <h3 className="font-heading font-700 text-[#0A1628] text-base mb-2">{s.title}</h3>
                <p className="text-[#0A1628]/55 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/hoe-het-werkt" className="inline-flex items-center gap-2 text-[#3B9EE8] font-600 text-sm transition-colors cursor-pointer whitespace-nowrap hover:text-[#2D85C5]">
              Bekijk het volledige proces <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Voordelen */}
      <section ref={voordelenRef} className="py-20 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <h2 className="font-heading font-800 text-white text-3xl md:text-4xl mb-4">
              Waarom kiezen voor <span className="text-[#3B9EE8]">goedkoop inkopen</span><br />uit China via DUC?
            </h2>
            <p className="text-white/50 text-base max-w-xl mx-auto">
              DUC is uw betrouwbare partner voor goedkoop inkopen — van sourcing tot levering aan uw deur.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {voordelen.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                animate={voordelenInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white/5 border border-white/8 rounded-xl p-6"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#3B9EE8]/15 mb-4">
                  <i className={`${v.icon} text-[#3B9EE8] text-lg`} />
                </div>
                <h3 className="font-heading font-700 text-white text-base mb-2">{v.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Producten categorieën */}
      <section ref={productenRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
              <i className="ri-apps-2-line" /> Alle categorieën
            </span>
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-4">
              Welke producten kunt u<br />
              <span className="text-[#3B9EE8]">goedkoop inkopen uit China?</span>
            </h2>
            <p className="text-[#0A1628]/55 text-base max-w-xl mx-auto">
              Van kleding tot elektronica — vrijwel elk product is goedkoper in te kopen uit China via DUC.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {producten.map((p, i) => (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={productenInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.35, delay: i * 0.05 }}
              >
                <Link
                  to={p.href}
                  className="flex flex-col items-center gap-3 bg-[#F7F8FA] border border-[#0A1628]/6 rounded-xl p-6 hover:border-[#3B9EE8]/40 hover:bg-[#3B9EE8]/4 transition-all cursor-pointer group"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#3B9EE8]/12 group-hover:bg-[#3B9EE8]/20 transition-colors">
                    <i className={`${p.icon} text-[#3B9EE8] text-lg`} />
                  </div>
                  <span className="text-[#0A1628]/75 text-sm font-600 text-center leading-snug">{p.label}</span>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/diensten" className="inline-flex items-center gap-2 text-[#3B9EE8] font-600 text-sm transition-colors cursor-pointer whitespace-nowrap hover:text-[#2D85C5]">
              Bekijk al onze diensten <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonial / social proof */}
      <section className="py-16 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <h2 className="font-heading font-800 text-[#0A1628] text-2xl md:text-3xl mb-3">
              Meer dan <span className="text-[#3B9EE8]">100 bedrijven</span> kopen al goedkoop in via DUC
            </h2>
            <p className="text-[#0A1628]/55 text-base max-w-lg mx-auto">
              Van kleine horecazaken tot grote retailers — iedereen kan profiteren van goedkoop inkopen uit China.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { naam: 'Sehgal Juwelier', sector: 'Juweliers', quote: 'Via DUC kopen wij onze sieradendozen en verpakkingen ruim 70% goedkoper in dan bij onze Nederlandse leverancier. Kwaliteit is minstens gelijk.', icon: 'ri-vip-diamond-line' },
              { naam: 'Lagaetano', sector: 'Horeca', quote: 'Bedrukte servetten en takeaway verpakkingen goedkoop ingekocht via DUC. Levertijd was prima en de service was uitstekend. Wij zijn vaste klant.', icon: 'ri-restaurant-line' },
              { naam: 'Kamal Beauty Center', sector: 'Beauty', quote: 'Branded towels en schorten voor ons salon. DUC regelde alles — van design tot levering aan onze deur. Goedkoper dan alles wat wij eerder hadden gevonden.', icon: 'ri-scissors-cut-line' },
            ].map((t) => (
              <div key={t.naam} className="bg-white border border-[#0A1628]/6 rounded-xl p-7">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B9EE8]/12 flex-shrink-0">
                    <i className={`${t.icon} text-[#3B9EE8] text-lg`} />
                  </div>
                  <div>
                    <div className="font-700 text-[#0A1628] text-sm">{t.naam}</div>
                    <div className="text-[#0A1628]/40 text-xs">{t.sector}</div>
                  </div>
                  <div className="ml-auto flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-xs" />
                    ))}
                  </div>
                </div>
                <p className="text-[#0A1628]/65 text-sm leading-relaxed italic">&ldquo;{t.quote}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-3">
              Vragen over <span className="text-[#3B9EE8]">goedkoop inkopen uit China?</span>
            </h2>
            <p className="text-[#0A1628]/55 text-base">Alles wat u wilt weten over goedkoop inkopen, kwaliteit en levering.</p>
          </div>
          <div className="space-y-2">
            {faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
          </div>
          <div className="text-center mt-8">
            <Link to="/faq" className="inline-flex items-center gap-2 text-[#3B9EE8] font-600 text-sm transition-colors cursor-pointer whitespace-nowrap hover:text-[#2D85C5]">
              Bekijk alle veelgestelde vragen <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Interne links sectie */}
      <section className="py-16 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-heading font-700 text-[#0A1628] text-xl md:text-2xl text-center mb-8">
            Meer lezen over <span className="text-[#3B9EE8]">inkopen uit China?</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { href: '/inkopen-uit-china', icon: 'ri-global-line', title: 'Inkopen uit China', desc: 'Alles over importeren en inkopen uit China voor Nederlandse bedrijven.' },
              { href: '/sourcing-service', icon: 'ri-search-eye-line', title: 'Sourcing Service', desc: 'Hoe onze professionele sourcing service uw inkoop volledig ontzorgt.' },
              { href: '/private-label-china', icon: 'ri-vip-crown-line', title: 'Private Label China', desc: 'Goedkoop inkopen uit China met uw eigen logo en branding.' },
            ].map((l) => (
              <Link key={l.href} to={l.href} className="flex items-start gap-4 bg-white border border-[#0A1628]/6 rounded-xl p-6 hover:border-[#3B9EE8]/40 transition-all group cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B9EE8]/12 group-hover:bg-[#3B9EE8]/20 transition-colors flex-shrink-0 mt-0.5">
                  <i className={`${l.icon} text-[#3B9EE8] text-lg`} />
                </div>
                <div>
                  <div className="font-700 text-[#0A1628] text-sm mb-1">{l.title}</div>
                  <div className="text-[#0A1628]/55 text-xs leading-relaxed">{l.desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A1628]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-800 text-white text-3xl md:text-4xl mb-4">
            Klaar om <span className="text-[#3B9EE8]">goedkoop in te kopen</span><br />uit China?
          </h2>
          <p className="text-white/55 text-base mb-8 leading-relaxed max-w-xl mx-auto">
            Vraag nu een gratis en vrijblijvende prijsindicatie aan. Binnen 24 uur weet u hoeveel u kunt besparen met goedkoop inkopen uit China via DUC.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-8 py-4 rounded-lg font-700 transition-all cursor-pointer whitespace-nowrap">
              Gratis prijsindicatie aanvragen
              <i className="ri-arrow-right-line" />
            </Link>
            <a
              href="https://wa.me/31624359162?text=Hi%2C%20ik%20wil%20graag%20goedkoop%20inkopen%20uit%20China%20via%20DUC."
              target="_blank" rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-[#3B9EE8]/60 text-white hover:text-[#3B9EE8] px-7 py-4 rounded-lg font-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl" /> WhatsApp direct
            </a>
          </div>
          <div className="mt-10 pt-8 border-t border-white/10 flex flex-wrap justify-center gap-x-8 gap-y-3">
            {[
              { icon: 'ri-check-line', text: 'Gratis & vrijblijvend' },
              { icon: 'ri-time-line', text: 'Reactie binnen 24 uur' },
              { icon: 'ri-shield-check-line', text: 'Geen verplichtingen' },
              { icon: 'ri-lock-line', text: 'Vertrouwelijk behandeld' },
            ].map((i) => (
              <div key={i.text} className="flex items-center gap-2">
                <i className={`${i.icon} text-[#3B9EE8] text-sm`} />
                <span className="text-white/50 text-sm">{i.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
